/**
 * Sales Reporting Dashboard Page
 * 
 * Main dashboard for sales agents to:
 * - View their performance summary
 * - See their pipeline
 * - Track follow-ups
 * - Log activities
 * - Use AI chatbot
 * - View their AI-generated report
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { progressiveFetch } from '../utils/progressiveFetch';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import SiteVisitTracker, { SiteVisitReportDialog } from '../components/SiteVisitTracker';
import TeamSiteVisitsPanel from '../components/TeamSiteVisitsPanel';
import SiteVisitReportsPanel from '../components/SiteVisitReportsPanel';
import MyMarketingDashboard from './MyMarketingDashboard';
import HelpInfo from '../components/HelpInfo';
import DirhamSign from '../components/DirhamSign';
import {
  BarChart3,
  TrendingUp,
  Users,
  Phone,
  Calendar,
  Clock,
  MapPin,
  MessageSquare,
  FileText,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  RefreshCw,
  ChevronRight,
  Send,
  LogOut,
  Home,
  Info,
  X,
  Plus,
  ClipboardList,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The Log Field Visit form collects a 12-hour clock time ("02:30") plus an
// AM/PM period. The backend's OOO model only accepts a single ISO timestamp
// in `expected_return_at`, so we must combine them before posting — without
// this the return time the agent types is silently dropped on the wire.
// Returns an ISO string for today at that time, or null if unparseable.
const buildExpectedReturnAt = (timeStr, period) => {
  const raw = (timeStr || '').trim();
  const m = raw.match(/^(\d{1,2}):?(\d{0,2})$/);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = m[2] ? parseInt(m[2], 10) : 0;
  if (isNaN(hours) || hours < 1 || hours > 12 || isNaN(minutes) || minutes > 59) {
    return null;
  }
  const p = (period || 'AM').toUpperCase();
  if (p === 'PM' && hours < 12) hours += 12;
  if (p === 'AM' && hours === 12) hours = 0;
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
};

// Live elapsed-time card shown while the agent is out of office. Ticks every
// second so the timer is always current. Uses `left_at` (ISO from the API)
// as the starting point.
const OOOActiveCard = ({ currentOOO, onReturn, isDarkMode }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const startMs = (() => {
    const raw = currentOOO?.left_at || currentOOO?.created_at;
    if (!raw) return null;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : null;
  })();
  const elapsedSecs = startMs ? Math.max(0, Math.floor((now - startMs) / 1000)) : null;
  const fmt = (s) => {
    if (s == null) return '';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(r).padStart(2, '0')}s`;
    if (m > 0) return `${m}m ${String(r).padStart(2, '0')}s`;
    return `${r}s`;
  };

  const expectedReturnMs = (() => {
    if (!currentOOO?.expected_return_at) return null;
    const t = new Date(currentOOO.expected_return_at).getTime();
    return Number.isFinite(t) ? t : null;
  })();
  const expectedReturn = expectedReturnMs != null
    ? new Date(expectedReturnMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;
  // Past the time the agent said they'd be back — surface it so they
  // (and anyone glancing at the dashboard) can see the visit is running long.
  const isOverdue = expectedReturnMs != null && now > expectedReturnMs;

  return (
    <Card className={isDarkMode ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <MapPin className="text-amber-500" size={20} />
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                Currently Out of Office
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                {currentOOO.reason?.replace(/_/g, ' ')} at {currentOOO.destination}
              </p>
              {elapsedSecs != null && (
                <p className={`text-xs mt-1 font-mono ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  <Clock size={12} className="inline mr-1" />
                  Out for {fmt(elapsedSecs)}
                  {expectedReturn && (
                    <>
                      {' · Expected back '}{expectedReturn}
                      {isOverdue && (
                        <span className="text-red-600 dark:text-red-400 font-semibold">
                          {' · overdue'}
                        </span>
                      )}
                    </>
                  )}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={onReturn}
            variant="outline"
            className={isDarkMode ? 'border-amber-600 text-amber-300' : 'border-amber-500 text-amber-700'}
          >
            <Home size={16} className="mr-2" />
            I'm Back
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// OOO Logger Component
const OOOLogger = ({ onOOOLog, currentOOO, onReturn }) => {
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const [isLogging, setIsLogging] = useState(false);
  const [formData, setFormData] = useState({
    reason: 'site_visit',
    destination: '',
    expected_return_time: '',
    expected_return_period: 'AM',
    notes: ''
  });

  // "Request Company Car" sub-form on the Log Field Visit card.
  // Disabled by default; the fields only appear when the agent toggles
  // it on. Submission of the field visit also POSTs the car request when
  // enabled — see handleSubmit.
  const [carRequest, setCarRequest] = useState({
    enabled: false,
    pickup_date: '',          // YYYY-MM-DD
    pickup_time: '',          // HH:MM (12h)
    pickup_period: 'AM',
    pickup_location: 'Office',  // 'Office' | 'Other'
    pickup_location_other: '',
    dropoff_location: '',     // pre-filled from destination on submit
    passengers: 1,
    car_notes: '',
  });
  // Location options are reason-specific — site_visit pulls registered
  // property geofences, broker_meeting pulls brokers, client_meeting pulls
  // contacts. "__other__" always exists as a free-text fallback so the agent
  // is never blocked when the source list is empty or doesn't have the row
  // they need yet. Fetched on first need and cached per reason.
  const [locationCache, setLocationCache] = useState({
    site_visit: null,
    broker_meeting: null,
    client_meeting: null,
  });
  const [locationSelect, setLocationSelect] = useState('');
  // Searchable Location picker — the agent types the property / broker /
  // client name and the list filters as they spell it, instead of
  // scrolling a long static dropdown. `locQuery` is the text in the
  // box; `locOpen` controls the suggestion list.
  const [locQuery, setLocQuery] = useState('');
  const [locOpen, setLocOpen] = useState(false);
  // Address / coordinates of the currently-chosen location — set on
  // every selection path (CRM record, geocoded map address, or cleared
  // for free-text). Drives the "View on map" link.
  const [locMeta, setLocMeta] = useState({ address: '', lat: null, lng: null });
  // Live map-address search results (OpenStreetMap / Nominatim) so the
  // agent can pick any real-world address, not just registered CRM
  // records. Debounced as they type.
  const [addrResults, setAddrResults] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  // Toggle for the "How this works" instructional panel that drops down
  // when the user clicks the (i) icon next to the form title. Defaults
  // closed so the form stays compact for repeat users; first-time users
  // open it once and find the step-by-step guide.
  const [showHelp, setShowHelp] = useState(false);

  // `labelPlural` is carried explicitly because naive "label + s"
  // pluralisation breaks "property" → "propertys". Each entry spells
  // its own plural so the picker reads correctly for every reason.
  const reasonMeta = {
    site_visit: { label: 'property', labelPlural: 'properties', placeholder: 'Select a property' },
    broker_meeting: { label: 'broker', labelPlural: 'brokers', placeholder: 'Select a broker' },
    client_meeting: { label: 'client', labelPlural: 'clients', placeholder: 'Select a client' },
    other: { label: 'location', labelPlural: 'locations', placeholder: 'Type the location below' },
  };

  useEffect(() => {
    const reason = formData.reason;
    if (!token || reason === 'other') return;
    if (locationCache[reason] !== null) return; // already fetched
    let cancelled = false;
    (async () => {
      try {
        let items = [];
        // Each option carries an `address` and `lat`/`lng` where the
        // source record has them, so the picker can show the map
        // address and a "View on map" link once a location is chosen.
        if (reason === 'site_visit') {
          const res = await fetch(`${API_URL}/api/site-visits/geofences?token=${encodeURIComponent(token)}`);
          const data = await res.json();
          if (data?.success) {
            items = (data.geofences || []).map((g) => ({
              id: g.id,
              name: g.name,
              address: g.address || g.formatted_address || g.location || '',
              lat: g.latitude ?? g.lat ?? null,
              lng: g.longitude ?? g.lng ?? null,
            }));
          }
        } else if (reason === 'broker_meeting') {
          const res = await fetch(`${API_URL}/api/brokers?token=${encodeURIComponent(token)}`);
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data?.brokers || []);
          items = list.map((b) => ({
            id: b.id || b._id,
            name: b.company ? `${b.name} · ${b.company}` : b.name,
            address: b.address || b.office_address || b.location || '',
            lat: b.latitude ?? b.lat ?? null,
            lng: b.longitude ?? b.lng ?? null,
          })).filter((x) => x.id && x.name);
        } else if (reason === 'client_meeting') {
          const res = await fetch(`${API_URL}/api/contacts?token=${encodeURIComponent(token)}&limit=200&sort_by=updated_at&sort_order=desc`);
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data?.contacts || []);
          items = list.map((c) => ({
            id: c.id || c._id,
            name: c.name || c.email || c.phone || 'Unnamed contact',
            address: c.address || [c.area, c.city, c.community].filter(Boolean).join(', ') || '',
            lat: c.latitude ?? c.lat ?? null,
            lng: c.longitude ?? c.lng ?? null,
          })).filter((x) => x.id);
        }
        if (!cancelled) {
          setLocationCache((prev) => ({ ...prev, [reason]: items }));
        }
      } catch (e) {
        if (!cancelled) {
          setLocationCache((prev) => ({ ...prev, [reason]: [] }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, formData.reason, locationCache]);

  // Geocode the typed text against OpenStreetMap so the agent can pick
  // a real map address with coordinates. Debounced (~450ms) and only
  // fired for queries of 3+ chars to stay within Nominatim's fair-use
  // limits. Skipped for the "Other" reason (free text only).
  useEffect(() => {
    const q = locQuery.trim();
    if (formData.reason === 'other' || q.length < 3) {
      setAddrResults([]);
      return;
    }
    setAddrLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=6&addressdetails=0&q=${encodeURIComponent(q)}`,
        );
        const data = await res.json();
        if (!cancelled) setAddrResults(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setAddrResults([]);
      } finally {
        if (!cancelled) setAddrLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [locQuery, formData.reason]);

  const locationOptions = locationCache[formData.reason] || [];
  const meta = reasonMeta[formData.reason] || reasonMeta.other;

  // Live validation of the Expected Return field — surfaced inline under
  // the input so the agent is corrected as they type, not only on submit.
  // Empty is left unflagged here (the red asterisk already marks it
  // required); we only complain once there's something to judge.
  const returnTimeError = (() => {
    const raw = (formData.expected_return_time || '').trim();
    if (!raw) return null;
    const iso = buildExpectedReturnAt(raw, formData.expected_return_period);
    if (!iso) return 'Enter the time as HH:MM — e.g. 02:30';
    if (new Date(iso).getTime() <= Date.now()) {
      return 'Return time must be later than now';
    }
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    // All fields are mandatory — block submission until each is filled.
    if (!formData.reason) {
      toast.error('Please pick a reason');
      return;
    }
    if (!formData.expected_return_time || !formData.expected_return_time.trim()) {
      toast.error('Please enter an expected return time');
      return;
    }
    if (!formData.expected_return_period) {
      toast.error('Please choose AM or PM for the return time');
      return;
    }
    if (!formData.destination || !formData.destination.trim()) {
      toast.error('Please enter a location');
      return;
    }

    // Combine the HH:MM + AM/PM inputs into the single ISO timestamp the
    // backend stores. If it can't be parsed, stop here — a field visit
    // with no return time leaves the HOD unable to track the agent.
    const expectedReturnAt = buildExpectedReturnAt(
      formData.expected_return_time,
      formData.expected_return_period,
    );
    if (!expectedReturnAt) {
      toast.error('Enter the return time as HH:MM — e.g. 02:30');
      return;
    }

    // The expected return must be in the future — a field visit can't be
    // due back at a clock time that has already passed today. This catches
    // mistakes like logging at 2:10 PM with a "02:00 PM" return, which
    // would otherwise show the agent as overdue the instant they log out.
    if (new Date(expectedReturnAt).getTime() <= Date.now()) {
      toast.error('Expected return time must be later than now');
      return;
    }

    setIsLogging(true);
    try {
      await onOOOLog({ ...formData, expected_return_at: expectedReturnAt });
      // If the agent also asked for a company car, submit the car
      // request right after the OOO log so it's linked semantically
      // (same trip, same destination). A failure here is non-fatal —
      // the field visit is already logged; we just warn the agent so
      // they can re-submit the car request if needed.
      if (carRequest.enabled) {
        try {
          const pickupAt = buildExpectedReturnAt(
            carRequest.pickup_time,
            carRequest.pickup_period,
          );
          if (!pickupAt) {
            throw new Error('Invalid pickup time — use HH:MM');
          }
          // If a pickup_date was provided, combine it with the time.
          let pickupAtIso = pickupAt;
          if (carRequest.pickup_date) {
            const t = new Date(pickupAt);
            const [y, mo, d] = carRequest.pickup_date.split('-').map(Number);
            t.setFullYear(y, mo - 1, d);
            pickupAtIso = t.toISOString();
          }
          const pickupLoc = carRequest.pickup_location === 'Other'
            ? (carRequest.pickup_location_other || 'Other').trim()
            : 'Office';
          const dropoffLoc = (carRequest.dropoff_location || formData.destination || '').trim();
          if (!dropoffLoc) throw new Error('Drop-off location is required');
          await fetch(`${API_URL}/api/car-requests?token=${encodeURIComponent(token)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pickup_at: pickupAtIso,
              pickup_location: pickupLoc,
              dropoff_location: dropoffLoc,
              passengers: carRequest.passengers,
              notes: carRequest.car_notes,
            }),
          }).then(async (r) => {
            if (!r.ok) {
              const err = await r.json().catch(() => ({}));
              throw new Error(err.detail || `Car request HTTP ${r.status}`);
            }
          });
          toast.success('Field visit logged · car request sent to receptionist');
        } catch (carErr) {
          toast.warning(
            `Field visit logged, but car request failed: ${carErr.message || carErr}`
          );
        }
      } else {
        toast.success('Field visit logged');
      }
      setFormData({ reason: 'site_visit', destination: '', expected_return_time: '', expected_return_period: 'AM', notes: '' });
      setCarRequest({
        enabled: false, pickup_date: '', pickup_time: '', pickup_period: 'AM',
        pickup_location: 'Office', pickup_location_other: '',
        dropoff_location: '', passengers: 1, car_notes: '',
      });
      setLocationSelect('');
      setLocQuery('');
      setLocMeta({ address: '', lat: null, lng: null });
      setAddrResults([]);
    } catch (error) {
      toast.error('Failed to log field visit');
    }
    setIsLogging(false);
  };

  if (currentOOO) {
    return (
      <OOOActiveCard currentOOO={currentOOO} onReturn={onReturn} isDarkMode={isDarkMode} />
    );
  }

  return (
    <Card className={isDarkMode ? 'bg-card border-border' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin size={18} />
          Log Field Visit
          {/* Info icon — toggles the step-by-step instructional panel
              below. Closed by default so the form stays compact; new
              users open it once to learn the flow, repeat users
              never see it. */}
          <button
            type="button"
            onClick={() => setShowHelp((s) => !s)}
            aria-label="How does this work?"
            title="How does this work?"
            className={`ml-auto inline-flex items-center justify-center w-7 h-7 rounded-full transition ${
              showHelp
                ? 'bg-violet-100 text-violet-700'
                : isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            <Info size={16} />
          </button>
        </CardTitle>
      </CardHeader>
      {/* Step-by-step instructions panel — collapsible, mounted just
          above the form so the cause-and-effect reads top-to-bottom:
          1) read the steps, 2) fill the fields in the same order. */}
      {showHelp && (
        <div className={`mx-6 mb-3 rounded-lg border p-4 text-sm ${
          isDarkMode
            ? 'bg-violet-900/10 border-violet-700/40 text-slate-200'
            : 'bg-violet-50 border-violet-200 text-slate-700'
        }`}>
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold flex items-center gap-1.5">
              <Info size={14} /> How to log a field visit
            </p>
            <button
              type="button"
              onClick={() => setShowHelp(false)}
              aria-label="Close instructions"
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          </div>
          <ol className="list-decimal pl-5 mt-2 space-y-1.5">
            <li>
              <strong>Pick a reason</strong> — Site Visit, Broker Meeting,
              Client Meeting, or Other. The Location dropdown below
              changes its source based on this choice.
            </li>
            <li>
              <strong>Set your expected return</strong> — the time you
              think you'll be back in office. Your HOD uses this on the
              In &amp; Out tab; if you don't return by then, it flags as
              "no return time set".
            </li>
            <li>
              <strong>Choose a location</strong> from the dropdown:
              <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5">
                <li><em>Site Visit</em> → registered property geofences. Picking one enables GPS arrival/departure auto-detect.</li>
                <li><em>Broker Meeting</em> → your brokers list.</li>
                <li><em>Client Meeting</em> → your assigned contacts.</li>
                <li><em>Other</em> (or empty list) → switch to <strong>Other</strong> at the bottom of the dropdown and type a free-text destination.</li>
              </ul>
            </li>
            <li>
              <strong>Click "Log Field Visit"</strong> — your status flips
              to <em>In Field</em> (you'll see the badge change at the top
              right), and the visit is logged for your HOD's dashboard.
            </li>
            <li>
              <strong>When you're back</strong>, open the dashboard again
              and click <em>Return to Office</em> on the active visit card.
              For Site Visit reasons, the post-visit report dialog opens
              automatically so you can record the outcome.
            </li>
          </ol>
          <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Tip: leave the form on this dashboard — closing the tab won't
            cancel the log; the visit stays open until you tap Return.
          </p>
        </div>
      )}
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`text-sm font-medium ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>
                Reason <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.reason}
                onValueChange={(v) => {
                  setFormData({ ...formData, reason: v, destination: '' });
                  setLocationSelect('');
                  setLocQuery('');
                  setLocMeta({ address: '', lat: null, lng: null });
                  setAddrResults([]);
                }}
              >
                <SelectTrigger className={`w-full mt-1 h-10 ${
                  isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-200'
                }`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="broker_meeting">Broker Meeting</SelectItem>
                  <SelectItem value="site_visit">Site Visit</SelectItem>
                  <SelectItem value="client_meeting">Client Meeting</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`text-sm font-medium ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>
                Expected Return <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="text"
                  value={formData.expected_return_time || ''}
                  onChange={(e) => {
                    // Allow only numbers and colon, format as HH:MM
                    let val = e.target.value.replace(/[^0-9:]/g, '');
                    if (val.length === 2 && !val.includes(':') && formData.expected_return_time?.length < 2) {
                      val = val + ':';
                    }
                    if (val.length <= 5) {
                      setFormData({...formData, expected_return_time: val});
                    }
                  }}
                  placeholder="02:30"
                  maxLength={5}
                  aria-invalid={Boolean(returnTimeError)}
                  className={`flex-1 h-10 px-3 rounded-md border text-sm text-center ${
                    returnTimeError
                      ? 'border-red-500 focus:ring-red-500'
                      : isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-500'
                        : 'bg-white border-slate-200 text-slate-900'
                  }`}
                />
                <Select
                  value={formData.expected_return_period || 'AM'}
                  onValueChange={(v) => setFormData({...formData, expected_return_period: v})}
                >
                  <SelectTrigger className={`w-24 h-10 ${
                    isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-200'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Inline validation — corrects the agent at the field
                  itself rather than waiting for a submit-time toast. */}
              {returnTimeError && (
                <p className="text-xs text-red-500 mt-1">{returnTimeError}</p>
              )}
            </div>
          </div>
          <div>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>
              Location <span className="text-red-500">*</span>
            </label>
            {formData.reason === 'other' ? (
              <input
                type="text"
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
                placeholder="Where are you going?"
                className={`w-full mt-1 h-10 px-3 rounded-md border text-sm ${
                  isDarkMode
                    ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400'
                    : 'bg-white border-slate-200 text-slate-900'
                }`}
                required
              />
            ) : (
              <>
                {/* Searchable location picker — type to filter the
                    property / broker / client list by spelling, instead
                    of scrolling a static dropdown. */}
                <div className="relative mt-1">
                  <input
                    type="text"
                    value={locQuery}
                    onChange={(e) => { setLocQuery(e.target.value); setLocOpen(true); }}
                    onFocus={() => setLocOpen(true)}
                    onBlur={() => setTimeout(() => setLocOpen(false), 150)}
                    placeholder={locationOptions.length
                      ? `Type to search ${meta.labelPlural}…`
                      : `No ${meta.labelPlural} yet — choose Other`}
                    className={`w-full h-10 px-3 rounded-md border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                  {locOpen && (
                    <div className={`absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border shadow-lg ${
                      isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
                    }`}>
                      {(() => {
                        const q = locQuery.trim().toLowerCase();
                        const matches = q
                          ? locationOptions.filter((o) => (o.name || '').toLowerCase().includes(q))
                          : locationOptions;
                        const sectionHdr = `px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide ${
                          isDarkMode ? 'text-slate-500' : 'text-slate-400'
                        }`;
                        return (
                          <>
                            {/* Registered CRM records (properties / brokers / clients) */}
                            {matches.length > 0 && (
                              <div className={sectionHdr}>Registered {meta.labelPlural}</div>
                            )}
                            {matches.map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setLocationSelect(opt.id);
                                  setLocQuery(opt.name || '');
                                  setFormData({ ...formData, destination: opt.name || '' });
                                  setLocMeta({
                                    address: opt.address || '',
                                    lat: opt.lat ?? null,
                                    lng: opt.lng ?? null,
                                  });
                                  setLocOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm ${
                                  isDarkMode ? 'text-white hover:bg-slate-700' : 'text-slate-900 hover:bg-slate-100'
                                } ${locationSelect === opt.id ? (isDarkMode ? 'bg-slate-700' : 'bg-violet-50') : ''}`}
                              >
                                {opt.name}
                                {opt.address && (
                                  <span className={`block text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {opt.address}
                                  </span>
                                )}
                              </button>
                            ))}

                            {/* Live map-address search (OpenStreetMap) */}
                            {(addrLoading || addrResults.length > 0) && (
                              <div className={`${sectionHdr} border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                Map addresses
                              </div>
                            )}
                            {addrLoading && (
                              <p className={`px-3 py-1.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Searching the map…
                              </p>
                            )}
                            {addrResults.map((r) => (
                              <button
                                key={r.place_id}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const lat = parseFloat(r.lat);
                                  const lng = parseFloat(r.lon);
                                  setLocationSelect('__address__');
                                  setLocQuery(r.display_name);
                                  setFormData({ ...formData, destination: r.display_name });
                                  setLocMeta({ address: r.display_name, lat, lng });
                                  setLocOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm flex items-start gap-1.5 ${
                                  isDarkMode ? 'text-white hover:bg-slate-700' : 'text-slate-900 hover:bg-slate-100'
                                }`}
                              >
                                <MapPin size={13} className="mt-0.5 flex-shrink-0 text-rose-500" />
                                <span className="flex-1 min-w-0">{r.display_name}</span>
                              </button>
                            ))}

                            {q && matches.length === 0 && !addrLoading && addrResults.length === 0 && (
                              <p className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                No {meta.label} or map address matches "{locQuery}"
                              </p>
                            )}
                            {/* Free-text fallback — always available so the
                                agent is never blocked by a missing record. */}
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setLocationSelect('__other__');
                                setLocQuery('');
                                setFormData({ ...formData, destination: '' });
                                setLocMeta({ address: '', lat: null, lng: null });
                                setLocOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-t ${
                                isDarkMode
                                  ? 'text-violet-300 border-slate-600 hover:bg-slate-700'
                                  : 'text-violet-600 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              Other (type a custom location)
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                {(locationSelect === '__other__' || (!locationSelect && !locationOptions.length)) && (
                  <input
                    type="text"
                    value={formData.destination}
                    onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    placeholder={`Type a ${meta.label} name`}
                    className={`w-full mt-2 h-10 px-3 rounded-md border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                )}
                {/* Map link — once a location is set, show its address
                    and a link that opens it on Google Maps. Coordinates
                    (from a CRM record or a picked map address) are used
                    when available; otherwise the typed text is searched. */}
                {(() => {
                  const addr = locMeta.address || '';
                  const hasCoords = locMeta.lat != null && locMeta.lng != null;
                  const dest = (formData.destination || '').trim();
                  if (!dest && !hasCoords) return null;
                  const query = hasCoords
                    ? `${locMeta.lat},${locMeta.lng}`
                    : (addr || dest);
                  if (!query) return null;
                  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
                  return (
                    <div className={`mt-2 flex items-start gap-1.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <MapPin size={13} className="mt-0.5 flex-shrink-0 text-rose-500" />
                      <span className="flex-1 min-w-0">
                        {addr || dest}
                        {hasCoords && (
                          <span className="ml-1 opacity-70">({Number(locMeta.lat).toFixed(4)}, {Number(locMeta.lng).toFixed(4)})</span>
                        )}
                      </span>
                      <a
                        href={mapUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-shrink-0 text-violet-600 hover:text-violet-700 underline"
                      >
                        View on map
                      </a>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          {/* "Request Company Car" — collapsible sub-form. Disabled by
              default; the fields render when toggled on. On submit the
              car request is POSTed right after the OOO log so it's
              attached to the same trip. */}
          <div className={`rounded-lg border ${
            isDarkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-slate-50'
          }`}>
            <label className="flex items-center gap-2 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={carRequest.enabled}
                onChange={(e) => setCarRequest((s) => ({ ...s, enabled: e.target.checked }))}
                className="h-4 w-4 accent-violet-600"
              />
              <span className="text-sm font-medium">Request company car</span>
              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Receptionist will approve or counter-propose.
              </span>
            </label>
            {carRequest.enabled && (
              <div className="p-3 pt-0 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">
                      Pickup date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={carRequest.pickup_date}
                      onChange={(e) => setCarRequest((s) => ({ ...s, pickup_date: e.target.value }))}
                      className={`w-full mt-1 h-9 px-3 rounded-md border text-sm ${
                        isDarkMode
                          ? 'bg-slate-800 border-slate-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">
                      Pickup time <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={carRequest.pickup_time}
                        onChange={(e) => {
                          let v = e.target.value.replace(/[^0-9:]/g, '');
                          if (v.length === 2 && !v.includes(':') && carRequest.pickup_time?.length < 2) {
                            v += ':';
                          }
                          if (v.length <= 5) {
                            setCarRequest((s) => ({ ...s, pickup_time: v }));
                          }
                        }}
                        placeholder="02:30"
                        maxLength={5}
                        className={`flex-1 h-9 px-3 rounded-md border text-sm text-center ${
                          isDarkMode
                            ? 'bg-slate-800 border-slate-600 text-white'
                            : 'bg-white border-slate-200 text-slate-900'
                        }`}
                      />
                      <Select
                        value={carRequest.pickup_period}
                        onValueChange={(v) => setCarRequest((s) => ({ ...s, pickup_period: v }))}
                      >
                        <SelectTrigger className="h-9 w-20"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">AM</SelectItem>
                          <SelectItem value="PM">PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Pickup location</label>
                    <Select
                      value={carRequest.pickup_location}
                      onValueChange={(v) => setCarRequest((s) => ({ ...s, pickup_location: v }))}
                    >
                      <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Office">Office</SelectItem>
                        <SelectItem value="Other">Other (custom)</SelectItem>
                      </SelectContent>
                    </Select>
                    {carRequest.pickup_location === 'Other' && (
                      <input
                        type="text"
                        value={carRequest.pickup_location_other}
                        onChange={(e) => setCarRequest((s) => ({ ...s, pickup_location_other: e.target.value }))}
                        placeholder="Where to pick you up"
                        className={`w-full mt-1.5 h-9 px-3 rounded-md border text-sm ${
                          isDarkMode
                            ? 'bg-slate-800 border-slate-600 text-white'
                            : 'bg-white border-slate-200 text-slate-900'
                        }`}
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Passengers</label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={carRequest.passengers}
                      onChange={(e) => setCarRequest((s) => ({
                        ...s, passengers: Math.max(1, Number(e.target.value) || 1),
                      }))}
                      className={`w-full mt-1 h-9 px-3 rounded-md border text-sm ${
                        isDarkMode
                          ? 'bg-slate-800 border-slate-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600">
                    Drop-off location
                    <span className={`ml-1.5 text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      (auto-uses your visit destination if blank)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={carRequest.dropoff_location}
                    onChange={(e) => setCarRequest((s) => ({ ...s, dropoff_location: e.target.value }))}
                    placeholder={formData.destination || 'Drop-off address'}
                    className={`w-full mt-1 h-9 px-3 rounded-md border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
                  <textarea
                    value={carRequest.car_notes}
                    onChange={(e) => setCarRequest((s) => ({ ...s, car_notes: e.target.value }))}
                    rows={2}
                    placeholder="Anything the receptionist should know — e.g. urgent, returning with client…"
                    className={`w-full mt-1 px-3 py-2 rounded-md border text-sm ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLogging || Boolean(returnTimeError)}
            className="w-full"
          >
            {isLogging ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <LogOut size={16} className="mr-2" />}
            Log Field Visit
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// AI Chat Panel Component
const AIChatPanel = ({ token }) => {
  const { isDarkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const text = input;
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/agent/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: text,
          // Strip extra fields — the backend's Pydantic model only allows
          // string values on history entries, so boolean flags like
          // activity_logged / report_updated from previous assistant
          // bubbles trigger a 422 Validation failed if we forward them.
          conversation_history: messages.slice(-10).map((m) => ({
            role: String(m.role || ''),
            content: String(m.content || ''),
          })),
        })
      });
      let data = null;
      try { data = await response.json(); } catch { /* not json */ }

      if (response.ok && data?.success) {
        const reply = data?.data?.reply || data?.reply || '(empty reply)';
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: reply,
          activity_logged: data?.data?.activity_logged,
          report_updated: data?.data?.report_updated,
        }]);
      } else {
        const msg = data?.detail || data?.error || `Chat failed (HTTP ${response.status})`;
        toast.error(msg);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ ${msg}`,
          error: true,
        }]);
      }
    } catch (error) {
      console.error('[AI_CHAT] send failed', error);
      toast.error(`Failed to send: ${error.message || error}`);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ Network error: ${error.message || error}`,
        error: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className={`flex flex-col h-[500px] ${isDarkMode ? 'bg-card border-border' : ''}`}>
      <CardHeader className="pb-3 border-b">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <MessageSquare size={18} />
          AI Assistant
          <HelpInfo
            align="inline"
            title="How to use the AI Assistant"
            steps={[
              'Type natural-language updates like "Met Mr. Khan today, he wants 3BR Marina view" — the AI parses and logs the activity to the right lead automatically.',
              'Ask for analysis: "Which of my deals are at risk?" or "Summarize my pipeline" — the assistant pulls from your live data.',
              'Use it to draft messages: "Write a follow-up email to Sarah about Skyline 459".',
              <>Activities it logs end up in your <em>Activity Logs</em> and on the lead's timeline — your HOD sees the same record.</>,
            ]}
            tip="The shorter and clearer your message, the better the parse. One activity per message works best."
          />
        </CardTitle>
        <CardDescription>Report activities and get insights</CardDescription>
      </CardHeader>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.length === 0 && (
            <p className={`text-sm text-center ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
              Tell me what you've been working on today...
            </p>
          )}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : isDarkMode ? 'bg-muted text-foreground' : 'bg-slate-100 text-slate-900'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.activity_logged && (
                  <Badge variant="outline" className="mt-2 text-xs">Activity logged</Badge>
                )}
                {msg.report_updated && (
                  <Badge variant="outline" className="mt-2 ml-1 text-xs">Report updated</Badge>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className={`rounded-lg px-4 py-2 ${isDarkMode ? 'bg-muted' : 'bg-slate-100'}`}>
                <RefreshCw size={16} className="animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type your update..."
            className={`flex-1 px-3 py-2 rounded-md border ${
              isDarkMode ? 'bg-background border-border text-foreground' : 'bg-white border-slate-200'
            }`}
          />
          <Button onClick={sendMessage} disabled={isLoading}>
            <Send size={16} />
          </Button>
        </div>
      </div>
    </Card>
  );
};

// My Report Card Component
const MyReportCard = ({ report, onRefresh, isRefreshing }) => {
  const { isDarkMode } = useTheme();

  if (!report) {
    return (
      <Card className={isDarkMode ? 'bg-card border-border' : ''}>
        {/* Same header + help icon as the populated state so first-time
            users can read the "what is this?" explanation BEFORE they
            generate their first report — the empty state was previously
            help-less, which made the page feel like a black box. */}
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <FileText size={18} />
            My Daily Report
            <HelpInfo
              align="inline"
              title="How My Daily Report works"
              steps={[
                'The AI summarizes your day automatically based on your logged activities, calls, meetings, and lead updates — you don\'t write this report yourself.',
                'Click Generate Report below to produce today\'s summary. The AI compiles what you\'ve done so far and structures it into sections.',
                <>The <strong>HOD viewed</strong> badge appears once your HOD opens the report. If it's missing by end of day, your HOD hasn't seen it yet.</>,
                'Click Refresh later to regenerate the report after logging more activity — useful right before leaving for the day.',
                'Report sections: Daily Summary, Pipeline Progress, Lead Engagement, Follow-up Status, Flags & Attention.',
              ]}
              tip="Log activities throughout the day, not all at end-of-day — the AI captures more detail when entries are fresh."
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 text-center">
          <FileText size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-300'}`} />
          <p className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}>
            No report generated yet for today
          </p>
          <Button onClick={onRefresh} className="mt-4" disabled={isRefreshing}>
            {isRefreshing ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
            Generate Report
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isDarkMode ? 'bg-card border-border' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <FileText size={18} />
            My Daily Report
            <HelpInfo
              align="inline"
              title="How My Daily Report works"
              steps={[
                'The AI summarizes your day automatically based on your logged activities, calls, meetings, and lead updates — you don\'t write this report yourself.',
                <>The <strong>HOD viewed</strong> badge appears once your HOD opens the report. If it's missing by end of day, your HOD hasn't seen it yet.</>,
                'Click Refresh to regenerate the report after logging more activity — useful right before leaving for the day.',
                'The report has structured sections: Daily Summary, Pipeline Progress, Lead Engagement, Follow-up Status, Flags & Attention.',
              ]}
              tip="Log activities throughout the day, not at end-of-day — the AI captures more detail when entries are fresh."
            />
          </CardTitle>
          <div className="flex items-center gap-2">
            {report.hod_viewed && (
              <Badge variant="outline" className="text-xs text-emerald-600">
                <CheckCircle2 size={12} className="mr-1" />
                Seen by HOD
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              v{report.version}
            </Badge>
          </div>
        </div>
        <CardDescription>
          Last updated: {new Date(report.generated_at).toLocaleTimeString()}
          <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
            isDarkMode ? 'bg-muted' : 'bg-slate-100'
          }`}>
            {report.trigger_type?.replace(/_/g, ' ')}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <div className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
            <div dangerouslySetInnerHTML={{ 
              __html: report.report_text?.replace(/\n/g, '<br/>').replace(/##/g, '<h3>').replace(/-/g, '•') 
            }} />
          </div>
        </ScrollArea>
        <div className="mt-4 pt-4 border-t">
          <p className={`text-xs ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
            This report is live and updates automatically when you log activities or chat.
          </p>
          <Button onClick={onRefresh} variant="outline" size="sm" className="mt-2" disabled={isRefreshing}>
            {isRefreshing ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            Refresh Report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Format a task's due date together with its time (when available).
const formatTaskDue = (task) => {
  if (!task?.due_date) return 'No due date';
  let dt;
  try {
    const raw = String(task.due_date);
    // If a separate due_time exists and the date has no time component, combine them.
    if (task.due_time && /^\d{4}-\d{2}-\d{2}$/.test(raw.slice(0, 10)) && raw.length <= 10) {
      dt = new Date(`${raw.slice(0, 10)}T${task.due_time}`);
    } else {
      dt = new Date(raw);
    }
  } catch {
    return String(task.due_date);
  }
  if (isNaN(dt.getTime())) return String(task.due_date);
  const datePart = dt.toLocaleDateString();
  // Show a time only when the task actually carries one.
  const hasTime =
    !!task.due_time ||
    dt.getHours() !== 0 ||
    dt.getMinutes() !== 0;
  if (!hasTime) return datePart;
  const timePart = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${datePart}, ${timePart}`;
};

// Visual style for the "who assigned this" badge.
const TASK_SOURCE_STYLES = {
  You: 'bg-slate-200 text-slate-700',
  HOD: 'bg-indigo-100 text-indigo-700',
  CEO: 'bg-amber-100 text-amber-700',
};

const TaskSourceBadge = ({ source }) => {
  if (!source) return null;
  const label = source === 'You' ? 'Created by you' : `Assigned by ${source}`;
  const cls = TASK_SOURCE_STYLES[source] || 'bg-slate-200 text-slate-700';
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>
      {label}
    </span>
  );
};

// Main Dashboard Component
const SalesReportingDashboard = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();

  const [summary, setSummary] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  // Leads assigned to this agent that haven't progressed into a deal
  // yet. Surfaced as a "New Leads" tab inside My Pipeline so the agent
  // can spot fresh assignments without leaving the dashboard.
  const [myNewLeads, setMyNewLeads] = useState([]);
  const [followups, setFollowups] = useState(null);
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [tasks, setTasks] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [unreadFeedback, setUnreadFeedback] = useState(0);
  
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // After "I'm Back", we open the visit report dialog with the closed
  // mirrored site visit so the agent can submit an outcome while it's fresh.
  const [reportFor, setReportFor] = useState(null);
  // Bumped whenever a visit report is submitted so the "My Site Visit
  // Reports" panel re-fetches and shows the new report within a second.
  const [reportRefreshKey, setReportRefreshKey] = useState(0);

  // Feedback card filters — date range + sender role (All / CEO / HOD).
  // The unread badge always reflects the full list; filters only narrow what
  // is rendered.
  const [fbFrom, setFbFrom] = useState('');
  const [fbTo, setFbTo] = useState('');
  const [fbFromRole, setFbFromRole] = useState('all');

  // ---- Add-task / Add-follow-up dialog ----------------------------------
  // Same form for both flows — only task_type differs. When the CEO / HOD
  // isn't involved the agent uses this to jot down their own reminders so
  // the Follow-ups & Tasks card doesn't only surface work delegated from
  // above.
  const [taskDialogMode, setTaskDialogMode] = useState(null); // 'task' | 'follow_up' | null
  const [savingNewTask, setSavingNewTask] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    priority: 'medium',
  });
  const resetNewTaskForm = () => setNewTaskForm({
    title: '', description: '', due_date: '', due_time: '', priority: 'medium',
  });
  const openTaskDialog = (mode) => {
    resetNewTaskForm();
    setTaskDialogMode(mode);
  };
  const closeTaskDialog = () => {
    setTaskDialogMode(null);
    resetNewTaskForm();
  };
  const handleCreateNewTask = async () => {
    if (!newTaskForm.title.trim()) {
      toast.error('Give the task a title');
      return;
    }
    setSavingNewTask(true);
    try {
      // POST /api/tasks accepts the token as a query param and the body
      // as a Task model. When due_time is given, we roll it into the ISO
      // due_date so the backend stores a single datetime (matches how
      // Overdue vs Upcoming is bucketed on the server).
      let dueISO = null;
      if (newTaskForm.due_date) {
        const timePart = newTaskForm.due_time || '09:00';
        // Local time, then to ISO — matches what an agent expects when
        // they picked "5 Jul, 2:30 PM" in their own timezone.
        dueISO = new Date(`${newTaskForm.due_date}T${timePart}:00`).toISOString();
      }
      const res = await fetch(
        `${API_URL}/api/tasks?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title:       newTaskForm.title.trim(),
            description: newTaskForm.description.trim(),
            due_date:    dueISO,
            priority:    newTaskForm.priority,
            task_type:   taskDialogMode === 'follow_up' ? 'follow_up' : 'other',
            status:      'pending',
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || err?.error || 'Failed to save');
        return;
      }
      toast.success(taskDialogMode === 'follow_up' ? 'Follow-up added' : 'Task added');
      closeTaskDialog();
      fetchData();
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSavingNewTask(false);
    }
  };

  // Progressive fetch — each endpoint paints its tile as soon as it lands
  // and one slow call (e.g. /reports/me when an AI generation is happening
  // server-side) no longer blocks the whole page on a spinner.
  const fetchData = useCallback(() => {
    if (!token) return;
    progressiveFetch({
      onFirstResponse: () => setLoading(false),
      safetyMs: 3000,
      logPrefix: '[MyDashboard]',
      tasks: [
        { url: `${API_URL}/api/agent/summary`,            auth: token, onData: (d) => d?.success && setSummary(d.data) },
        { url: `${API_URL}/api/agent/pipeline`,           auth: token, onData: (d) => d?.success && setPipeline(d.data) },
        { url: `${API_URL}/api/agent/follow-ups`,         auth: token, onData: (d) => d?.success && setFollowups(d.data) },
        { url: `${API_URL}/api/agent/reports/me`,         auth: token, onData: (d) => d?.success && setReport(d.data?.report) },
        { url: `${API_URL}/api/agent/status`,             auth: token, onData: (d) => d?.success && setStatus(d.data) },
        { url: `${API_URL}/api/agent/activity-logs?limit=20`, auth: token, onData: (d) => d?.success && setActivityLogs(d.data?.logs || []) },
        { url: `${API_URL}/api/agent/tasks`,              auth: token, onData: (d) => d?.success && setTasks(d.data) },
        { url: `${API_URL}/api/agent/feedback`,           auth: token, onData: (d) => {
          if (d?.success) {
            setFeedback(d.data?.feedback || []);
            setUnreadFeedback(d.data?.unread_count || 0);
          }
        } },
        // My new leads — contacts assigned to me whose status indicates
        // they haven't entered the working pipeline yet. We ask the
        // backend for "all_my" ownership (every lead I own regardless
        // of actionable flag) and then keep the few that look like new
        // leads. Token goes in the query string because /api/contacts
        // historically accepts only that form.
        {
          url: `${API_URL}/api/contacts?token=${encodeURIComponent(token)}&ownership_filter=all_my&limit=100`,
          // The contacts endpoint doesn't require the Bearer header (it
          // reads the token query param), but passing it costs nothing
          // and matches the rest of the dashboard's calls.
          auth: token,
          onData: (d) => {
            const list = Array.isArray(d) ? d : (d?.contacts || []);
            const isNew = (c) => {
              const s = (c.status || '').toLowerCase();
              return !s || ['new', 'subscriber', 'lead', 'active', 'pending_qualification'].includes(s);
            };
            setMyNewLeads(list.filter(isNew));
          },
        },
      ],
    });
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleOOOLog = async (data) => {
    const response = await fetch(`${API_URL}/api/agent/ooo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const result = await response.json();
    if (result.success) {
      fetchData();
    } else {
      throw new Error(result.error);
    }
  };

  const handleReturn = async () => {
    if (!status?.current_ooo?.id) return;

    const response = await fetch(`${API_URL}/api/agent/ooo/${status.current_ooo.id}/return`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const result = await response.json();
    if (result.success) {
      toast.success('Welcome back!');
      // If the OOO was a mirrored site visit, prompt for a quick report.
      if (result.closed_visit) setReportFor(result.closed_visit);
      fetchData();
    }
  };

  const handleRefreshReport = async () => {
    setIsRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/agent/reports/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setReport(result.data?.report);
        toast.success('Report refreshed');
      }
    } catch (error) {
      toast.error('Failed to refresh report');
    }
    setIsRefreshing(false);
  };

  const handleReplyToFeedback = async (feedbackId, replyText) => {
    try {
      const response = await fetch(`${API_URL}/api/agent/feedback/${feedbackId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reply_text: replyText })
      });
      const result = await response.json();
      if (result.success) {
        setFeedback(prev => prev.map(f => f.id === feedbackId ? result.data.feedback : f));
        toast.success('Reply sent');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleMarkFeedbackRead = async (feedbackId) => {
    try {
      await fetch(`${API_URL}/api/agent/feedback/${feedbackId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFeedback(prev => prev.map(f => f.id === feedbackId ? {...f, is_read_by_agent: true} : f));
      setUnreadFeedback(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark feedback as read:', error);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-background' : 'bg-slate-50'}`}>
        <RefreshCw className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-background' : 'bg-slate-50'}`}>
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-foreground' : 'text-slate-900'}`}>
              Sales Dashboard
            </h1>
            <p className={isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}>
              Welcome back, {user?.full_name || user?.email}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* In Office / Out of Office status badge is sales-only —
                marketing roles don't have field-tracking and the
                badge is meaningless for them. Hidden for any role
                containing "marketing"; everyone else still sees their
                live attendance status. */}
            {!((user?.role || '').toLowerCase().includes('marketing')
                || (user?.email || '').toLowerCase().includes('marketing')) && (
              <Badge variant={status?.status?.current_status === 'ooo' ? 'warning' : 'default'}>
                {(() => {
                  const s = status?.status?.current_status;
                  if (s === 'ooo') return 'Out of Office';
                  if (s === 'in_meeting') return 'In Meeting';
                  if (s === 'on_break') return 'On Break';
                  if (s === 'available') return 'Available';
                  return s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Active';
                })()}
              </Badge>
            )}
          </div>
        </div>

        {/* Field-visit widgets (Log Field Visit, Site Visit Tracker,
            Team Site Visits, My Site Visit Reports) are sales-only —
            they don't apply to marketing users, so we hide the whole
            block for anyone whose role OR email contains "marketing".
            The email fallback catches test accounts like
            marketing@test.com that intentionally use the sales_agent
            role for permission parity but shouldn't see geofence
            widgets. */}
        {(() => {
          const role = (user?.role || '').toLowerCase();
          const email = (user?.email || '').toLowerCase();
          const isMarketing = role.includes('marketing') || email.includes('marketing');
          if (isMarketing) return null;
          const isManagerPlus = [
            'admin', 'super_admin', 'owner', 'account_owner',
            'ceo', 'hod', 'hod_sales', 'manager',
          ].includes(role);
          return (
            <>
              {/* OOO Logger */}
              <div className="mb-6">
                <OOOLogger
                  onOOOLog={handleOOOLog}
                  currentOOO={status?.current_ooo}
                  onReturn={handleReturn}
                />
              </div>

              {/* Auto GPS Detection — agent self-tracker for everyone,
                  plus the team panel for CEO / HOD / managers. */}
              <div className="mb-6 space-y-4">
                <SiteVisitTracker />
                {isManagerPlus && <TeamSiteVisitsPanel />}
                <SiteVisitReportsPanel scope="mine" refreshKey={reportRefreshKey} />
              </div>
            </>
          );
        })()}

        {/* Performance Summary Cards moved to "My Dashboard Overview"
            (AgentOverviewDashboard) — Total Leads, Active Deals,
            Conversion Rate and Activities Today now live there as the
            agent's headline KPIs, so this working dashboard isn't
            duplicating them. */}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Pipeline & Follow-ups */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pipeline */}
            <Card className={isDarkMode ? 'bg-card border-border' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  <Briefcase size={18} />
                  My Pipeline
                  <HelpInfo
                    align="inline"
                    title="How to read My Pipeline"
                    steps={[
                      'The tabs across the top let you filter your deals by stage (All / New Leads / prospecting / qualification / negotiation / proposal / closing).',
                      'Click any tab to see only deals in that stage and how many you have there.',
                      'Each row shows the deal name, value, and current stage.',
                      <>The amber <strong>Stale</strong> badge means the deal hasn't moved in over 7 days — those are the ones to act on first.</>,
                      'Click a deal row to open its detail page where you can update the stage, add notes, and log activities.',
                    ]}
                    tip="The New Leads tab is freshly-assigned leads that haven't entered the working pipeline yet. Cold (3+ days) leads are tinted amber."
                  />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Render the deal rows for either a single stage or every
                  // stage merged. Pulled out so the All / per-stage tabs can
                  // share the same row markup.
                  const renderDeals = (entries) => (
                    entries.length === 0 ? (
                      <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                        No deals in this stage.
                      </p>
                    ) : entries.map(({ deal, stage }) => (
                      <div
                        key={deal.id}
                        className={`p-3 rounded-lg mb-2 ${
                          deal.is_stale
                            ? isDarkMode ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'
                            : isDarkMode ? 'bg-muted' : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-foreground' : 'text-slate-900'}`}>
                              {deal.name || 'Untitled Deal'}
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                              <DirhamSign /> {deal.value?.toLocaleString() || 0} • {stage}
                            </p>
                          </div>
                          {deal.is_stale && (
                            <Badge variant="warning" className="text-xs">
                              <AlertCircle size={12} className="mr-1" />
                              Stale
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))
                  );

                  const stageEntries = pipeline?.stages ? Object.entries(pipeline.stages) : [];
                  const allDeals = stageEntries.flatMap(([stage, deals]) => deals.map((d) => ({ deal: d, stage })));

                  // Friendly relative "X days ago" helper for new-lead
                  // rows — leads that haven't been touched in 3+ days
                  // get an amber badge so the agent can spot the cold
                  // ones first.
                  const daysSince = (iso) => {
                    if (!iso) return null;
                    const d = new Date(iso);
                    if (Number.isNaN(d.getTime())) return null;
                    return Math.floor((Date.now() - d.getTime()) / 86400000);
                  };

                  return (
                    <Tabs defaultValue="all">
                      <TabsList>
                        <TabsTrigger value="all">All ({pipeline?.total_deals || 0})</TabsTrigger>
                        {/* New leads tab — sits between All and the
                            stage tabs because new leads are the very
                            start of the funnel; this keeps the visual
                            flow left-to-right (New → prospecting →
                            qualification → ... → closing). */}
                        <TabsTrigger value="__new_leads__">
                          New Leads ({myNewLeads.length})
                        </TabsTrigger>
                        {stageEntries.map(([stage, deals]) => (
                          <TabsTrigger key={stage} value={stage}>
                            {stage} ({deals.length})
                          </TabsTrigger>
                        ))}
                      </TabsList>

                      <TabsContent value="all" className="mt-4">
                        <ScrollArea className="h-[300px]">
                          {renderDeals(allDeals)}
                        </ScrollArea>
                      </TabsContent>

                      <TabsContent value="__new_leads__" className="mt-4">
                        <ScrollArea className="h-[300px]">
                          {myNewLeads.length === 0 ? (
                            <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                              No new leads assigned to you yet. Fresh leads will land here.
                            </p>
                          ) : myNewLeads.map((lead) => {
                            const since = daysSince(lead.assigned_at || lead.created_at);
                            const cold = since != null && since >= 3;
                            return (
                              <div
                                key={lead.id}
                                className={`p-3 rounded-lg mb-2 cursor-pointer hover:opacity-90 transition ${
                                  cold
                                    ? isDarkMode ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'
                                    : isDarkMode ? 'bg-muted' : 'bg-slate-50'
                                }`}
                                onClick={() => { if (lead.id) window.location.href = `/contacts/${lead.id}`; }}
                                title="Open lead"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <p className={`font-medium truncate ${isDarkMode ? 'text-foreground' : 'text-slate-900'}`}>
                                      {lead.name || lead.full_name || 'Untitled lead'}
                                    </p>
                                    <p className={`text-sm truncate ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                                      {lead.source || lead.lead_source || 'unknown source'}
                                      {lead.email && <> • {lead.email}</>}
                                      {since != null && <> • {since === 0 ? 'today' : `${since}d ago`}</>}
                                    </p>
                                  </div>
                                  {cold && (
                                    <Badge variant="warning" className="text-xs flex-shrink-0">
                                      <AlertCircle size={12} className="mr-1" />
                                      Cold {since}d
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </ScrollArea>
                      </TabsContent>

                      {stageEntries.map(([stage, deals]) => (
                        <TabsContent key={stage} value={stage} className="mt-4">
                          <ScrollArea className="h-[300px]">
                            {renderDeals(deals.map((d) => ({ deal: d, stage })))}
                          </ScrollArea>
                        </TabsContent>
                      ))}
                    </Tabs>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Follow-ups */}
            <Card className={isDarkMode ? 'bg-card border-border' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <Calendar size={18} />
                    Follow-ups & Tasks
                    <HelpInfo
                      align="inline"
                      title="How to use Follow-ups & Tasks"
                      steps={[
                        <>This panel merges <strong>every</strong> task you need to act on: tasks you created yourself, tasks assigned to you by your HOD, and tasks assigned to you by the CEO. The badge on each row tells you where it came from.</>,
                        <>Each task shows its <strong>due date and time</strong> so you know exactly when it's expected — not just the day.</>,
                        <>The <strong>Overdue</strong> section lists tasks whose due time has already passed — work these first; they're the easiest trust to lose.</>,
                        <>The <strong>Upcoming</strong> section lists everything still ahead, soonest first, so you can plan your day.</>,
                        <>Use the <strong>Add Task</strong> and <strong>Add Follow-up</strong> buttons to jot down your own reminders — they land in the Upcoming section with the "Created by you" badge.</>,
                      ]}
                      tip="Snooze a follow-up only when you genuinely can't act today — the longer a lead waits, the colder it gets."
                    />
                  </CardTitle>
                  {/* Add-your-own controls. Separate buttons for the two
                      flows so the agent's intent lands in the record's
                      `task_type` field cleanly — same POST, different
                      classification. */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => openTaskDialog('task')}
                      data-testid="add-task-btn"
                    >
                      <ClipboardList size={14} className="mr-1.5" />
                      Add Task
                    </Button>
                    <Button
                      size="sm"
                      className="h-8 bg-violet-600 hover:bg-violet-700 text-white"
                      onClick={() => openTaskDialog('follow_up')}
                      data-testid="add-followup-btn"
                    >
                      <Plus size={14} className="mr-1.5" />
                      Add Follow-up
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {followups?.overdue?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-red-500 mb-2 flex items-center gap-1">
                        <AlertCircle size={14} />
                        Overdue ({followups.overdue.length})
                      </h4>
                      {followups.overdue.slice(0, 5).map((task, idx) => (
                        <div
                          key={task.id || `overdue-${idx}`}
                          className={`p-3 rounded-lg mb-2 ${
                            isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className={isDarkMode ? 'text-foreground' : 'text-slate-900'}>{task.title}</p>
                            <TaskSourceBadge source={task.source} />
                          </div>
                          <p className={`text-xs ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                            Due: {formatTaskDue(task)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {followups?.upcoming?.length > 0 && (
                    <div>
                      <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-600'}`}>
                        Upcoming ({followups.upcoming.length})
                      </h4>
                      {followups.upcoming.slice(0, 8).map((task, idx) => (
                        <div
                          key={task.id || `upcoming-${idx}`}
                          className={`p-3 rounded-lg mb-2 ${isDarkMode ? 'bg-muted' : 'bg-slate-50'}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className={isDarkMode ? 'text-foreground' : 'text-slate-900'}>{task.title}</p>
                            <TaskSourceBadge source={task.source} />
                          </div>
                          <p className={`text-xs ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                            Due: {formatTaskDue(task)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {(!followups?.overdue?.length && !followups?.upcoming?.length) && (
                    <p className={`text-center py-8 ${isDarkMode ? 'text-muted-foreground' : 'text-slate-500'}`}>
                      No pending follow-ups
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Feedback, Chat & Report */}
          <div className="space-y-6">
            {/* Feedback from Management */}
            {feedback.length > 0 && (() => {
              const fromMs = fbFrom ? new Date(`${fbFrom}T00:00:00`).getTime() : null;
              const toMs = fbTo ? new Date(`${fbTo}T23:59:59`).getTime() : null;
              const filteredFeedback = feedback.filter((fb) => {
                const role = (fb.sender_role || '').toLowerCase();
                if (fbFromRole === 'ceo' && role !== 'ceo') return false;
                if (fbFromRole === 'hod' && !role.startsWith('hod')) return false;
                const ts = new Date(fb.created_at).getTime();
                if (fromMs && ts < fromMs) return false;
                if (toMs && ts > toMs) return false;
                return true;
              });
              return (
              <Card className={isDarkMode ? 'bg-card border-border' : ''}>
                <CardHeader className="pb-3 space-y-2">
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <MessageSquare size={18} className="text-emerald-500" />
                    Feedback from Management
                    {unreadFeedback > 0 && (
                      <Badge className="bg-emerald-500 text-white ml-2">{unreadFeedback} new</Badge>
                    )}
                    <HelpInfo
                      align="inline"
                      title="How to use Feedback from Management"
                      steps={[
                        'Messages from your CEO or HOD land here — text comments and voice notes alike.',
                        'Use the filters above the list to narrow by sender role (CEO only / HOD only) or date range.',
                        'Click Reply to send a written response that the sender will see in their feedback thread.',
                        'Click the microphone to record a voice reply — useful when you\'re on the road and can\'t type.',
                        'Hit Mark as Read to clear the unread count once you\'ve handled the message.',
                      ]}
                      tip="Voice replies preserve tone — they're often the better choice for emotional or sensitive responses."
                    />
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <Select value={fbFromRole} onValueChange={setFbFromRole}>
                      <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All senders</SelectItem>
                        <SelectItem value="ceo">CEO only</SelectItem>
                        <SelectItem value="hod">HOD only</SelectItem>
                      </SelectContent>
                    </Select>
                    <input
                      type="date"
                      value={fbFrom}
                      onChange={(e) => setFbFrom(e.target.value)}
                      className={`h-8 px-2 rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                      title="From"
                    />
                    <span className="text-slate-400">to</span>
                    <input
                      type="date"
                      value={fbTo}
                      onChange={(e) => setFbTo(e.target.value)}
                      className={`h-8 px-2 rounded border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                      title="To"
                    />
                    {/* Always visible so the affordance is obvious;
                        dimmed when there's nothing to clear. */}
                    {(() => {
                      const atDefaults = !fbFrom && !fbTo && fbFromRole === 'all';
                      return (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={`h-8 text-xs ${
                            atDefaults
                              ? 'text-slate-400 hover:text-rose-600'
                              : 'text-rose-600 hover:text-rose-700'
                          }`}
                          onClick={() => { setFbFrom(''); setFbTo(''); setFbFromRole('all'); }}
                          title="Clear feedback filters"
                        >
                          Clear
                        </Button>
                      );
                    })()}
                    <span className="ml-auto text-slate-500">
                      Showing {filteredFeedback.length} of {feedback.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Native scroll — Radix ScrollArea was clipping content
                      because the inner viewport wasn't getting a height. */}
                  <div className="h-[420px] overflow-y-auto pr-2 space-y-4">
                    {filteredFeedback.length === 0 && (
                      <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        No feedback matches these filters.
                      </p>
                    )}
                    {filteredFeedback.map((fb) => (
                      <FeedbackItem
                        key={fb.id}
                        feedback={fb}
                        onReply={handleReplyToFeedback}
                        onMarkRead={handleMarkFeedbackRead}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
              );
            })()}
            <AIChatPanel token={token} />
            <MyReportCard
              report={report}
              onRefresh={handleRefreshReport}
              isRefreshing={isRefreshing}
            />
          </div>
        </div>
      </div>

      {reportFor && (
        <SiteVisitReportDialog
          visit={reportFor}
          token={token}
          onClose={() => setReportFor(null)}
          onSubmitted={() => setReportRefreshKey((k) => k + 1)}
        />
      )}

      {/* Add Task / Add Follow-up dialog — one form, two intents.
          The label and icon flip based on `taskDialogMode`. */}
      <Dialog
        open={!!taskDialogMode}
        onOpenChange={(open) => { if (!open) closeTaskDialog(); }}
      >
        <DialogContent className={`max-w-lg p-0 overflow-hidden ${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
          <DialogHeader className={`px-6 pt-6 pb-4 border-b ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'bg-gradient-to-r from-violet-50 to-white'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                {taskDialogMode === 'follow_up' ? <Calendar size={20} /> : <ClipboardList size={20} />}
              </div>
              <div className="flex-1">
                <DialogTitle className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                  {taskDialogMode === 'follow_up' ? 'Add Follow-up' : 'Add Task'}
                </DialogTitle>
                <DialogDescription className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {taskDialogMode === 'follow_up'
                    ? 'Schedule a reminder to circle back on a lead — lands in Upcoming under "Created by you".'
                    : 'Jot down a task for yourself — lands in Upcoming under "Created by you".'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Title
              </Label>
              <Input
                value={newTaskForm.title}
                onChange={(e) => setNewTaskForm({ ...newTaskForm, title: e.target.value })}
                placeholder={taskDialogMode === 'follow_up'
                  ? 'e.g. Call back Mrs. Suwaidi about Marina Heights'
                  : 'e.g. Prepare pricing sheet for Park Vista'}
                className="h-10"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Priority
              </Label>
              <Select
                value={newTaskForm.priority}
                onValueChange={(v) => setNewTaskForm({ ...newTaskForm, priority: v })}
              >
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Description
              </Label>
              <Textarea
                value={newTaskForm.description}
                onChange={(e) => setNewTaskForm({ ...newTaskForm, description: e.target.value })}
                placeholder="Optional notes to jog your memory"
                rows={3}
                className="resize-none"
              />
            </div>

            <div className={`rounded-lg border p-4 ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50/60'}`}>
              <div className={`text-[11px] uppercase tracking-wider font-semibold mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                When
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Due date</Label>
                  <Input
                    type="date"
                    value={newTaskForm.due_date}
                    onChange={(e) => setNewTaskForm({ ...newTaskForm, due_date: e.target.value })}
                    className={`h-10 ${isDarkMode ? '' : 'bg-white'}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Due time</Label>
                  <Input
                    type="time"
                    value={newTaskForm.due_time}
                    onChange={(e) => setNewTaskForm({ ...newTaskForm, due_time: e.target.value })}
                    className={`h-10 ${isDarkMode ? '' : 'bg-white'}`}
                  />
                </div>
              </div>
              <p className={`text-[11px] mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Leave blank for a no-due-date item — it'll show up under Upcoming.
              </p>
            </div>
          </div>

          <DialogFooter className={`px-6 py-4 border-t gap-2 ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'bg-slate-50/70'}`}>
            <Button variant="outline" onClick={closeTaskDialog} disabled={savingNewTask}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewTask}
              disabled={savingNewTask || !newTaskForm.title.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
            >
              {savingNewTask ? (
                <><RefreshCw size={14} className="mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Plus size={14} className="mr-2" /> {taskDialogMode === 'follow_up' ? 'Add Follow-up' : 'Add Task'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Records a voice reply and POSTs it to the feedback voice-reply endpoint.
// Mirrors the look of VoiceMessageRecorder's compact mic but uses a custom
// upload path so we hit `/api/agent/feedback/{id}/voice-reply` directly.
const FeedbackVoiceReplyButton = ({ feedbackId }) => {
  const { token } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const startTsRef = React.useRef(0);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      startTsRef.current = Date.now();
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const dur = Math.max(1, Math.round((Date.now() - startTsRef.current) / 1000));
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await upload(blob, dur);
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setIsRecording(true);
    } catch (e) {
      toast.error(`Mic error: ${e.message || e}`);
    }
  };
  const stop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };
  const upload = async (blob, durationSeconds) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'reply.webm');
      fd.append('duration_seconds', String(durationSeconds));
      const res = await fetch(`${API_URL}/api/agent/feedback/${feedbackId}/voice-reply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.success) toast.success('Voice reply sent');
      else toast.error(data.detail || 'Failed to send voice reply');
    } catch (e) {
      toast.error(`Upload failed: ${e.message || e}`);
    } finally {
      setUploading(false);
    }
  };

  if (uploading) {
    return (
      <Button size="sm" variant="outline" disabled>
        <RefreshCw size={14} className="animate-spin mr-1" />
        Sending…
      </Button>
    );
  }
  return isRecording ? (
    <Button size="sm" variant="destructive" onClick={stop}>
      ◼ Stop & send
    </Button>
  ) : (
    <Button size="sm" variant="outline" onClick={start} title="Record a voice reply">
      🎙 Voice reply
    </Button>
  );
};

// Feedback Item Component
const FeedbackItem = ({ feedback, onReply, onMarkRead, isDarkMode }) => {
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);
  const [sending, setSending] = useState(false);
  // Long messages get a See more / See less toggle so the inbox stays
  // skimmable. Threshold is intentionally low — anything longer than a
  // single sentence is collapsed by default.
  const [expanded, setExpanded] = useState(false);
  const TRUNCATE_AT = 200;
  const longText = (feedback.feedback_text || '').length > TRUNCATE_AT;
  const visibleText = longText && !expanded
    ? feedback.feedback_text.slice(0, TRUNCATE_AT).trimEnd() + '…'
    : feedback.feedback_text;

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    await onReply(feedback.id, replyText);
    setReplyText('');
    setShowReply(false);
    setSending(false);
    // Replying counts as engagement → mark this row read.
    if (!feedback.is_read_by_agent) onMarkRead(feedback.id);
  };

  // Read marker only fires when the user actively engages (opens reply,
  // expands long text, plays the voice). The green dot stays until then so
  // the agent can scroll the feed and still spot what's new.
  const markRead = () => {
    if (!feedback.is_read_by_agent) onMarkRead(feedback.id);
  };

  return (
    <div className={`p-3 rounded-lg border ${
      !feedback.is_read_by_agent
        ? isDarkMode ? 'bg-emerald-900/20 border-emerald-700' : 'bg-emerald-50 border-emerald-300'
        : isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium flex items-center gap-1.5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
            {!feedback.is_read_by_agent && (
              <span
                className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"
                title="New, unread"
              />
            )}
            From: {feedback.sender_name} ({feedback.sender_role?.replace(/_/g, ' ')})
          </p>
          {feedback.feedback_text && (
            <p className={`text-sm mt-1 whitespace-pre-wrap ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {visibleText}
              {longText && (
                <button
                  type="button"
                  onClick={() => { setExpanded((v) => !v); markRead(); }}
                  className={`ml-1 text-xs font-medium underline ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}
                >
                  {expanded ? 'See less' : 'See more'}
                </button>
              )}
            </p>
          )}
          {feedback.voice_message_id && (
            <div className={`mt-2 rounded-md p-2 ${isDarkMode ? 'bg-slate-800' : 'bg-white border border-slate-200'}`}>
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <span>🎙</span>
                <span>Voice note · {Math.round(feedback.voice_duration_seconds || 0)}s</span>
              </div>
              <audio
                controls
                preload="none"
                src={`${API_URL}/api/voice-messages/${feedback.voice_message_id}/audio`}
                className="w-full"
                onPlay={markRead}
              />
            </div>
          )}
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {new Date(feedback.created_at).toLocaleString()}
          </p>
        </div>
        {!feedback.is_read_by_agent && (
          <Badge className="bg-emerald-500 text-white text-xs">New</Badge>
        )}
      </div>

      {/* Agent's Reply (text or voice) */}
      {(feedback.agent_reply || feedback.agent_voice_reply_id) ? (
        <div className={`mt-3 pl-3 border-l-2 ${isDarkMode ? 'border-violet-500' : 'border-violet-400'}`}>
          <p className={`text-xs font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
            Your Reply:
          </p>
          {feedback.agent_reply && (
            <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {feedback.agent_reply}
            </p>
          )}
          {feedback.agent_voice_reply_id && (
            <div className={`mt-1 rounded-md p-2 ${isDarkMode ? 'bg-slate-800' : 'bg-white border border-slate-200'}`}>
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <span>🎙</span>
                <span>Voice reply · {Math.round(feedback.agent_voice_reply_duration || 0)}s</span>
              </div>
              <audio
                controls
                preload="none"
                src={`${API_URL}/api/voice-messages/${feedback.agent_voice_reply_id}/audio`}
                className="w-full"
              />
            </div>
          )}
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {feedback.agent_replied_at && new Date(feedback.agent_replied_at).toLocaleString()}
          </p>
        </div>
      ) : (
        <div className="mt-3">
          {showReply ? (
            <div className="space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write your reply..."
                rows={2}
                className={`w-full px-3 py-2 text-sm rounded-md border ${
                  isDarkMode
                    ? 'bg-slate-800 border-slate-600 text-white'
                    : 'bg-white border-slate-200 text-slate-900'
                }`}
              />
              <div className="flex gap-2 items-center flex-wrap">
                <Button
                  size="sm"
                  onClick={handleSendReply}
                  disabled={sending || !replyText.trim()}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  {sending ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                  Send Reply
                </Button>
                <FeedbackVoiceReplyButton feedbackId={feedback.id} />
                <Button size="sm" variant="ghost" onClick={() => setShowReply(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => { setShowReply(true); markRead(); }}>
              <MessageSquare size={14} className="mr-1" />
              Reply
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Marketing-context wrapper. When a marketing user lands on
// `/my-dashboard`, swap the sales-focused dashboard for the
// MyMarketingDashboard — the personal action-focused command center
// (today's leads, campaign alerts, recent leads, tasks, AI
// recommendations). The org-wide MarketingDashboardV2 still backs
// the management "Overview" route. Detected by either role or email
// containing "marketing" so test users like marketing@test.com
// (which use the sales_agent role for permission parity) route
// correctly.
const SalesOrMarketingDashboard = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const email = (user?.email || '').toLowerCase();
  const isMarketingUser = role.includes('marketing') || email.includes('marketing');
  if (isMarketingUser) {
    return <MyMarketingDashboard />;
  }
  return <SalesReportingDashboard />;
};

export default SalesOrMarketingDashboard;
